"use client";

import { appThemes } from "@pex/design-tokens";
import Image from "next/image";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";

export interface AccessDeniedProps {
  appName: string;
  className?: string;
  homeUrl?: string;
  manageAccountUrl?: string;
  message?: string;
}

export function AccessDenied({
  appName,
  homeUrl = "/",
  manageAccountUrl = "/manage",
  message,
  className,
}: AccessDeniedProps) {
  // Get app logo and display name based on app name
  const appKey = appName.toLowerCase() as keyof typeof appThemes;
  const theme = appThemes[appKey];
  const appLogo = theme && "logo" in theme ? theme.logo : undefined;
  const displayName = theme?.name || appName;

  const defaultMessage = `You don't have permission to access ${displayName}.`;

  return (
    <div
      className={`flex min-h-screen items-center justify-center p-4 ${className || ""}`}
    >
      <Card className="w-full max-w-md ring-0">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex aspect-square size-12 items-center justify-center rounded-lg bg-white p-3 shadow-sm">
            {appLogo ? (
              <div className="relative h-full w-full">
                <Image
                  alt={`${displayName} logo`}
                  className="object-contain"
                  fill
                  src={appLogo}
                />
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <span className="font-bold text-2xl text-muted-foreground">
                  {displayName[0]}
                </span>
              </div>
            )}
          </div>
          <CardTitle className="text-2xl">Access Denied</CardTitle>
          <CardDescription>{message || defaultMessage}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg bg-muted p-4">
            <p className="text-center text-muted-foreground text-sm">
              If you believe this is an error, please reach out to your
              organisation administrator or contact support at{" "}
              <a
                className="font-medium text-primary hover:underline"
                href="mailto:online@projectexodus.net"
              >
                online@projectexodus.net
              </a>
            </p>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-2">
          <Button asChild className="w-full">
            <a href={homeUrl}>Go Home</a>
          </Button>
          <Button asChild className="w-full" variant="outline">
            <a href={manageAccountUrl}>Manage Account</a>
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
